<template><div><!-- <component-combinator component-name="DtComboboxMultiSelect" /> -->
<code-example class="d-d-block" source-code='&lt;dt-combobox-multi-select
  ref=&quot;comboboxMultiSelect&quot;
  label=&quot;Label Text&quot;
  :selected-items=&quot;selectedItems&quot;
  @update:model-value=&quot;onInput&quot;
  @select=&quot;onSelect&quot;
  @remove=&quot;onRemove&quot;
&gt;
  &lt;template #list&gt;
    &lt;dt-stack as=&quot;ul&quot; class=&quot;d-ps-relative d-m-50 d-px-0&quot;&gt;
      &lt;dt-list-item
        v-for=&quot;(item, i) in items&quot;
        :key=&quot;item.id&quot;
        role=&quot;option&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;onSelect(i)&quot;
      &gt;
        {{ item.value }}
        &lt;template #right&gt;
          &lt;span class=&quot;d-fc-secondary&quot;&gt;{{ item.type }}&lt;/span&gt;
        &lt;/template&gt;
      &lt;/dt-list-item&gt;
    &lt;/dt-stack&gt;
  &lt;/template&gt;
&lt;/dt-combobox-multi-select&gt;'>
<dt-combobox-multi-select
  label="Label Text"
  :selected-items="selectedItems"
  @update:model-value="onComboboxInput"
  @select="onComboboxSelect"
  @remove="onComboboxRemove"
>
  <template #list>
    <dt-stack as="ul" class="d-ps-relative d-m-50 d-px-0">
      <dt-list-item
        v-for="(item, i) in items"
        :key="item.id"
        role="option"
        navigation-type="arrow-keys"
        @click="onComboboxSelect(i)"
      >
        {{ item.value }}
        <template #right>
          <span class="d-fc-secondary">{{ item.type }}</span>
        </template>
      </dt-list-item>
    </dt-stack>
  </template>
</dt-combobox-multi-select>
</code-example>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The Combobox Multi-Select component combines an input element with a dropdown list, allowing users to select multiple items. Selected items appear as chips within the input field.</p>
<h3 id="closing-the-list-after-selection" tabindex="-1" class="d-docsite--header-3">Closing the list after selection <a class="header-anchor d-link d-docsite--link d-link" href="#closing-the-list-after-selection" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When not passing <code v-pre>showList</code> and <code v-pre>hasSuggestionList</code> is <code v-pre>true</code>, to close the list with the <code v-pre>select</code> event, use the <code v-pre>closeComboboxList</code> method:</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token literal-property property">methods</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token function">onSelect</span> <span class="token punctuation">(</span><span class="token parameter">i</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token keyword">this</span><span class="token punctuation">.</span>$refs<span class="token punctuation">.</span>comboboxMultiSelect<span class="token punctuation">.</span><span class="token function">closeComboboxList</span><span class="token punctuation">(</span><span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div><h2 id="with-max-selected-validation" tabindex="-1" class="d-docsite--header-2">With Max Selected Validation <a class="header-anchor d-link d-docsite--link d-link" href="#with-max-selected-validation" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Adds validation for max selection. Make sure to provide the following props:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>maxSelected</code> the maximum number of selections you can make. 0 is unlimited</li>
<li class="d-docsite--list-element"><code v-pre>maxSelectedMessage</code> should be the message that shown if max selection has been reached</li>
</ul>
<code-example class="d-d-block" source-code='&lt;dt-combobox-multi-select
  ref=&quot;comboboxMultiSelect&quot;
  label=&quot;Label Text&quot;
  description=&quot;Select up to 2 options.&quot;
  :selected-items=&quot;selectedItems&quot;
  :max-selected=&quot;2&quot;
  :max-selected-message=&quot;[{ message: &#39;More than 2 selected&#39;, type: &#39;critical&#39; }]&quot;
  @update:model-value=&quot;onInput&quot;
  @select=&quot;onSelect&quot;
  @remove=&quot;onRemove&quot;
  @max-selected=&quot;onMaxSelected&quot;
&gt;
  &lt;template #list&gt;
    &lt;dt-stack as=&quot;ul&quot; class=&quot;d-ps-relative d-m-50 d-px-0&quot;&gt;
      &lt;dt-list-item
        v-for=&quot;(item, i) in items&quot;
        :key=&quot;item.id&quot;
        role=&quot;option&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;onSelect(i)&quot;
      &gt;
        {{ item.value }}
        &lt;template #right&gt;
          &lt;span class=&quot;d-fc-secondary&quot;&gt;{{ item.type }}&lt;/span&gt;
        &lt;/template&gt;
      &lt;/dt-list-item&gt;
    &lt;/dt-stack&gt;
  &lt;/template&gt;
&lt;/dt-combobox-multi-select&gt;'>
<dt-combobox-multi-select
  label="Label Text"
  description="Select up to 2 options."
  :selected-items="maxSelectedItems"
  :max-selected="2"
  :max-selected-message="[{ message: 'More than 2 selected', type: 'critical' }]"
  @update:model-value="onMaxSelectInput"
  @select="onMaxSelectSelect"
  @remove="onMaxSelectRemove"
  @max-selected="onMaxSelected"
>
  <template #list>
    <dt-stack as="ul" class="d-ps-relative d-m-50 d-px-0">
      <dt-list-item
        v-for="(item, i) in maxSelectItems"
        :key="item.id"
        role="option"
        navigation-type="arrow-keys"
        @click="onMaxSelectSelect(i)"
      >
        {{ item.value }}
        <template #right>
          <span class="d-fc-secondary">{{ item.type }}</span>
        </template>
      </dt-list-item>
    </dt-stack>
  </template>
</dt-combobox-multi-select>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="comboboxmultiselect" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A screen reader visible only close button is added by default.</p>
<h3 id="keyboard-support" tabindex="-1" class="d-docsite--header-3">Keyboard Support <a class="header-anchor d-link d-docsite--link d-link" href="#keyboard-support" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">User can navigate between chips pressing the <code v-pre>LEFT</code> and <code v-pre>RIGHT</code> key.</li>
<li class="d-docsite--list-element">Pressing <code v-pre>LEFT</code> key when you have chips in the input and you are at the start of the text would select the last chip.</li>
<li class="d-docsite--list-element">Pressing <code v-pre>RIGHT</code> key when you are at the last chip would focus on the start of the input.</li>
<li class="d-docsite--list-element">Pressing <code v-pre>BACKSPACE</code> key would focus the chip.</li>
<li class="d-docsite--list-element">When a chip is focused, pressing <code v-pre>BACKSPACE</code> or <code v-pre>DELETE</code> key would remove the chip.</li>
<li class="d-docsite--list-element">User can navigate the popover list pressing <code v-pre>UP</code> and <code v-pre>DOWN</code> key.</li>
</ul>
<p class="d-docsite--paragraph">See full <a href="/components/popover.html#keyboard-support" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Keyboard Support</a> for popover list.</p>
</div></template>


<script setup>
import { ref } from 'vue';

const ITEMS_LIST_DATA = [
  { id: 'item1', value: 'item1', type: 'MAINLINE' },
  { id: 'item2', value: 'item2', type: 'MAINLINE' },
  { id: 'item3', value: 'item3', type: 'MAINLINE' },
  { id: 'item4', value: 'item4', type: 'MAINLINE' },
  { id: 'item5', value: 'item5', type: 'MAINLINE' },
  { id: 'item6', value: 'item6', type: 'MAINLINE' },
  { id: 'item7', value: 'item7', type: 'MAINLINE' },
  { id: 'item8', value: 'item8', type: 'Other' },
];

const items = ref([...ITEMS_LIST_DATA]);
const selectedItems = ref([]);

const maxSelectItems = ref([...ITEMS_LIST_DATA]);
const maxSelectedItems = ref(['item1', 'item2', 'item3']);

function onComboboxInput (value) {
  items.value = ITEMS_LIST_DATA.filter(item => item.value.includes(value));
}

function onComboboxSelect (i) {
  if (items.value[i]) {
    const item = items.value[i].value;
    if (!selectedItems.value.includes(item)) {
      selectedItems.value.push(item);
    }
    items.value = [...ITEMS_LIST_DATA];
  }
}

function onComboboxRemove (item) {
  const index = selectedItems.value.indexOf(item);
  if (index >= 0) {
    selectedItems.value.splice(index, 1);
  }
}

function onMaxSelectInput (value) {
  maxSelectItems.value = ITEMS_LIST_DATA.filter(item => item.value.includes(value));
}

function onMaxSelectSelect (i) {
  if (maxSelectItems.value[i]) {
    const item = maxSelectItems.value[i].value;
    if (!maxSelectedItems.value.includes(item)) {
      maxSelectedItems.value.push(item);
    }
    maxSelectItems.value = [...ITEMS_LIST_DATA];
  }
}

function onMaxSelectRemove (item) {
  const index = maxSelectedItems.value.indexOf(item);
  if (index >= 0) {
    maxSelectedItems.value.splice(index, 1);
  }
}

function onMaxSelected () {
  console.log('Max selected reached');
}
</script>